import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import type { SlideSlot } from '@reelplicate/flows';
import { Button, Inline, Stack, Well } from '@reelplicate/ui';
import { useState } from 'react';

import { CaptionPreview } from '../../../caption-styles/caption-preview';
import {
  fallbackOption,
  SOURCE_LABELS,
  useCaptionStyleOptions,
} from '../../../caption-styles/caption-style-options';
import { useSlotImages } from '../../../caption-styles/slot-images';
import { type EditorStart, StyleEditor } from '../../../caption-styles/style-editor';
import { StylePicker } from '../../../caption-styles/style-picker';
import type { FieldSpec } from './config-fields';

/**
 * Carousel from slots' caption style (Figma "Flow editor · Caption style",
 * 395:1820): the look every slide uses unless a slide picks its own. Unset
 * follows the workspace's default. Change picks another; Customise builds a
 * style of your own from this one.
 */
export function CaptionStyleField({
  workspaceId,
  field,
  value,
  config,
  onChange,
}: {
  workspaceId: string;
  field: FieldSpec;
  value: unknown;
  config?: Record<string, unknown>;
  onChange: (value: unknown) => void;
}) {
  const ref = typeof value === 'string' ? value : undefined;
  const { data: workspaces } = useListWorkspaces();
  const workspaceDefault = workspaces?.find(
    (w) => w.id === workspaceId,
  )?.defaultCaptionStyle;
  const styles = useCaptionStyleOptions(workspaceId);
  const picked = styles.find(ref);
  const current = picked ?? fallbackOption(styles.find, workspaceDefault);
  const slots = (
    Array.isArray(config?.slots) ? config.slots : []
  ) as Partial<SlideSlot>[];
  const images = useSlotImages(
    workspaceId,
    slots.map((s) => s.imagesCollectionId),
  );
  const [editor, setEditor] = useState<EditorStart | null>(null);

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <span className="text-label-md text-text-primary">{field.label}</span>
        {field.hint && <p className="text-caption text-text-secondary">{field.hint}</p>}
      </Stack>
      {ref && styles.loaded && !picked && (
        <Well tone="warning" role="status">
          That style was deleted. Slides use the default until you pick another.
        </Well>
      )}
      <Inline
        gap={3}
        alignY="center"
        className="border-border-subtle bg-bg-surface rounded-lg border p-2.5"
      >
        <CaptionPreview
          style={current.style}
          imageUrl={images[0]}
          width={88}
          label={`${current.name} preview`}
        />
        <Stack gap={2} className="min-w-0 flex-1">
          <Stack gap={0}>
            <span className="text-body-sm-strong text-text-primary truncate">
              {current.name}
            </span>
            <span className="text-caption text-text-secondary">
              {picked ? SOURCE_LABELS[picked.source] : 'Workspace default'}
            </span>
          </Stack>
          <Inline gap={1.5}>
            <StylePicker
              title="Caption style · Every slide"
              options={styles.all}
              value={current.ref}
              imageUrl={images[0]}
              onSelect={(next) => onChange(next)}
              onNewStyle={() => setEditor({ mode: 'create', style: current.style })}
              trigger={
                <Button type="button" size="sm" variant="outline">
                  Change
                </Button>
              }
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() =>
                setEditor(
                  picked?.source === 'custom'
                    ? {
                        mode: 'edit',
                        id: picked.ref,
                        name: picked.name,
                        style: picked.style,
                      }
                    : {
                        mode: 'create',
                        name: `${current.name} (mine)`,
                        style: current.style,
                      },
                )
              }
            >
              Customise
            </Button>
          </Inline>
        </Stack>
      </Inline>
      <StyleEditor
        workspaceId={workspaceId}
        start={editor}
        images={images}
        onOpenChange={(open) => !open && setEditor(null)}
        onSaved={(saved) => onChange(saved.id)}
      />
    </Stack>
  );
}
